/* eslint-disable react-refresh/only-export-components -- useCurrentLangMeta is co-exported alongside the component by design; both use LANGUAGES */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Globe, X, Search, Check } from 'lucide-react';
import { useLang, loadLocale, LANGUAGES, type Lang } from '../../utils/i18n';
import { pathWithLang } from '../../utils/languages';
import { useFocusTrap } from '../../hooks/useFocusTrap';

/* ── Detect browser-preferred languages for "Suggested" section ── */
function getSuggestedCodes(): string[] {
  if (typeof navigator === 'undefined') return ['en'];
  const navLangs = Array.from(new Set(
    (navigator.languages ?? [navigator.language ?? 'en'])
      .map(l => l.split('-')[0].toLowerCase())
  ));
  return navLangs.slice(0, 4);
}

interface LanguageModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Full-screen language picker modal with search and suggested languages.
 *
 * Extracted from the old Header so both MegaNav and MobileDrawer can reuse it
 * without duplicating the modal DOM or the navigator-language detection.
 */
const LanguageModal: React.FC<LanguageModalProps> = ({ open, onClose }) => {
  const [langSearch, setLangSearch] = useState('');
  const { pathname, search, hash } = useLocation();
  const { lang, rememberLang, t } = useLang();
  const navigate = useNavigate();
  const modalRef  = useFocusTrap(open);
  const searchRef = useRef<HTMLInputElement>(null);


  const suggestedCodes = useMemo(getSuggestedCodes, []);
  const suggestedLangs = LANGUAGES.filter(l => suggestedCodes.includes(l.code));
  const filteredLangs  = langSearch.trim()
    ? LANGUAGES.filter(l =>
        l.label.toLowerCase().includes(langSearch.toLowerCase()) ||
        l.code.toLowerCase().includes(langSearch.toLowerCase())
      )
    : LANGUAGES;

  const close = () => { onClose(); setLangSearch(''); };

  /* Close modal on outside click */
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* Focus search box when modal opens */
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => searchRef.current?.focus(), 80);
    return () => clearTimeout(id);
  }, [open]);

  /* Escape key closes modal */
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickLang = async (code: Lang) => {
    rememberLang(code);
    close();
    // Keep the truthful current-language page visible while the small target
    // dictionary loads. Once cached, the URL and active locale can change as a
    // single guarded transition without showing the previous or English copy.
    try { await loadLocale(code); } catch { /* provider exposes its safe fallback */ }
    // This continuation runs after an async dictionary load, outside React's
    // discrete click event. Commit immediately so history cannot paint ahead
    // of the locale guard, document language, and structured-data cleanup.
    flushSync(() => {
      // <!-- SEO: this English guide links to the existing translated team tool. -->
      const targetPath = pathname.replace(/\/$/, '') === '/team-picker' && code !== 'en'
        ? '/random-team-generator/' : pathname;
      navigate(pathWithLang(targetPath, code) + search + hash, { replace: true });
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="language-dialog-title"
        className="relative w-full max-w-lg bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <Globe size={18} className="text-violet-400" />
            <span id="language-dialog-title" className="font-bold text-white text-base">{t('chrome.language')}</span>
          </div>
          <button
            onClick={close}
            aria-label={t('wheel.close')}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {/* Suggested languages */}
          {!langSearch && suggestedLangs.length > 0 && (
            <div className="px-5 pt-4 pb-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('chrome.suggestedLanguages')}</p>
              <div className="grid grid-cols-3 gap-1.5">
                {suggestedLangs.map(l => (
                  <button
                    key={l.code}
                    onClick={() => pickLang(l.code as Lang)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all text-start ${
                      lang === l.code
                        ? 'bg-violet-600 text-white font-semibold'
                        : 'text-gray-200 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <span className="text-base">{l.flag}</span>
                    <span className="truncate">{l.label}</span>
                    {lang === l.code && <Check size={13} className="ms-auto flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          {!langSearch && <div className="mx-5 border-t border-gray-800" />}

          {/* All languages with search */}
          <div className="px-5 pt-4 pb-4">
            {!langSearch && (
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('chrome.allLanguages')}</p>
            )}

            {/* Search */}
            <div className="relative mb-3">
              <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                ref={searchRef}
                type="text"
                placeholder={t('chrome.searchLanguages')}
                value={langSearch}
                onChange={e => setLangSearch(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg ps-8 pe-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>

            {/* Grid */}
            {filteredLangs.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">{t('chrome.noLanguages')}</p>
            ) : (
              <div className="grid grid-cols-3 gap-1.5">
                {filteredLangs.map(l => (
                  <button
                    key={l.code}
                    onClick={() => pickLang(l.code as Lang)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all text-start ${
                      lang === l.code
                        ? 'bg-violet-600 text-white font-semibold'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <span className="text-base flex-shrink-0">{l.flag}</span>
                    <span className="truncate">{l.label}</span>
                    {lang === l.code && <Check size={13} className="ms-auto flex-shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageModal;
/** The current language metadata, for rendering the globe button label. */
export function useCurrentLangMeta() {
  const { lang } = useLang();
  return LANGUAGES.find(l => l.code === lang) ?? LANGUAGES[0];
}
